// Copyright (c) 2026 杨翔宇（武汉文理学院）
// 物理智能教学智能体平台 (Physics Intelligent Agent)
// 保留所有权利 (All Rights Reserved)。未经著作权人书面许可，
// 不得以任何形式复制、传播、修改或用于商业用途。

import type { ArtifactEnvelope, Message } from '@/types';
import type { PhysicsAgentState } from '@/generated/physicsAgentState';

type UnknownRecord = Record<string, unknown>;

export interface UiStatePatch {
  messageId?: string;
  uiBlocks?: Message['ui_blocks'];
  artifacts?: ArtifactEnvelope[];
  visualizationState?: Message['visualization_state'];
  uiGenerationMode?: Message['ui_generation_mode'];
  solutionDelta?: string;
  actionRevision?: number;
  actionResult?: Record<string, unknown>;
  statePatch?: Partial<PhysicsAgentState>;
}

export type ChatRuntimeEvent = { type: 'ui_state_patch'; patch: UiStatePatch };

function asRecord(value: unknown): UnknownRecord | null {
  return value && typeof value === 'object' ? (value as UnknownRecord) : null;
}

function asString(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() ? value : undefined;
}

function asUiGenerationMode(value: unknown): Message['ui_generation_mode'] {
  return value === 'tool' || value === 'none' ? value : undefined;
}

function asActionRevision(value: unknown): number | undefined {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return Math.max(0, Math.trunc(value));
  }
  if (typeof value === 'string' && value.trim()) {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) {
      return Math.max(0, Math.trunc(parsed));
    }
  }
  return undefined;
}

export function isEmptyUiActionPatch(patch: UiStatePatch): boolean {
  return (
    patch.messageId === undefined &&
    patch.uiBlocks === undefined &&
    patch.artifacts === undefined &&
    patch.visualizationState === undefined &&
    patch.uiGenerationMode === undefined &&
    patch.solutionDelta === undefined &&
    patch.actionRevision === undefined &&
    patch.actionResult === undefined &&
    patch.statePatch === undefined
  );
}

export function normalizeUiActionRuntimeEvents(raw: unknown): ChatRuntimeEvent[] {
  const parsed = asRecord(raw);
  if (!parsed) {
    return [];
  }

  return [
    {
      type: 'ui_state_patch',
      patch: {
        messageId: asString(parsed.message_id),
        uiBlocks: Array.isArray(parsed.ui_blocks)
          ? (parsed.ui_blocks as Message['ui_blocks'])
          : undefined,
        artifacts: Array.isArray(parsed.artifacts)
          ? (parsed.artifacts as ArtifactEnvelope[])
          : undefined,
        visualizationState: (asRecord(parsed.visualization_state) ?? undefined) as unknown as
          | Message['visualization_state']
          | undefined,
        uiGenerationMode: asUiGenerationMode(parsed.ui_generation_mode),
        solutionDelta: asString(parsed.solution_delta),
        actionRevision: asActionRevision(parsed.action_revision),
        actionResult: asRecord(parsed.ui_action_result) ?? undefined,
        statePatch: (asRecord(parsed.state_patch) ?? undefined) as Partial<PhysicsAgentState> | undefined,
      },
    },
  ];
}
